import React from "react";
import { IconType } from "react-icons";
import { BsChevronDown } from "react-icons/bs";
import { createUseStyles } from "react-jss";
import { Link } from "react-router-dom";
import {
  KnownUsertype,
  KnownUsertypeName,
} from "../../api/credentialsAPI/usertype";
import { COLORS } from "../../assets/theme";
import { useAppSelector } from "../../hooks";
import {
  checkIfUsertypeAllowed,
  useUsertypePermission,
} from "hooks/auth/useUsertype";
import useMatchKiddo from "hooks/useMatchKiddo";

export interface KiddoElement {
  title: string;
  Icon?: IconType;
  shrinkTitle?: string;
  /**
   * if `undefined` or has length === 0, it will be allowed for everyone
   */
  allowedUsers?: (KnownUsertype | KnownUsertypeName)[];
  // subKiddos?: KiddoElement[];
  kiddoURL?: string;
}

export interface SidebarItemProps {
  title?: string;

  /**
   * if there is kiddo, clicking on parent item wont do any navigation
   */
  parentURL?: string;
  /**
   * if `undefined` or has length === 0, it will be allowed for everyone
   * @todo users will be complex, simple string might not suffice.
   */
  allowedUsers?: (KnownUsertype | KnownUsertypeName)[];
  Icon?: IconType;
  kiddos?: KiddoElement[];
}

const useStyles = createUseStyles(() => {
  return {
    container: {
      display: "flex",
      flexDirection: "column",
    },
    buttonStyle: {
      display: "flex",
      fontSize: 14,
      border: 0,
      alignItems: "center",
      padding: "0 1rem",
      color: "white",
      transition: "all .2s",
      "&.match": {
        color: COLORS.blue_1_puspenerbal,
        fontWeight: 600,
        "& svg": {
          color: COLORS.blue_1_puspenerbal,
          fill: COLORS.blue_1_puspenerbal,
        },
      },
      "&:hover": {
        color: COLORS.blue_1_puspenerbal,
        "& svg": {
          color: COLORS.blue_1_puspenerbal,
          fill: COLORS.blue_1_puspenerbal,
        },
      },
      "&:focus": {
        outline: "none",
        boxShadow: "none",
      },
      "&.shrink": {
        justifyContent: "space-between",
        paddingBottom: "10px",
        paddingTop: "10px",
      },
      textDecoration: "none",
      textTransform: "uppercase",
      letterSpacing: 0.45,
    },
    buttonExpandStyle: {
      display: "flex",
      border: 0,
      alignItems: "center",
      padding: "0 1rem",
      fontSize: 14,
      backgroundColor: "transparent",
      color: "white",
      textDecoration: "none",
      textTransform: "uppercase",
      letterSpacing: 0.45,
      transition: "all .2s",
      "&:hover": {
        color: COLORS.blue_1_puspenerbal,
        "& svg": {
          color: COLORS.blue_1_puspenerbal,
          fill: COLORS.blue_1_puspenerbal,
        },
      },
      "&:focus": {
        outline: "none",
        boxShadow: "none",
      },
      "&.shrink": {
        justifyContent: "space-between",
        paddingBottom: "10px",
        paddingTop: "10px",
      },
    },
    title: {
      flex: 1,
      margin: "1rem 0",
      textAlign: "left",
      overflowX: "hidden",
      "&.shrink": {
        textAlign: "center",
      },
    },
    chevron: {
      transform: "rotate(0deg)",
      transition: "transform .2s ease-in-out",
      "&.open": {
        transform: "rotate(-180deg)",
      },
    },
    kiddosContainer: {
      maxHeight: 0,
      transition: "all .3s",
      overflow: "hidden",
      "&.open": {
        maxHeight: "2000px", // need to be decided, scroll or view all?
      },
      "&::-webkit-scrollbar": {
        display: "none",
      },
    },
    aKiddo: {
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      padding: "0.35rem 1.6rem",
      textDecoration: "none",
      textTransform: "uppercase",
      letterSpacing: 0.45,
      fontSize: 14,
      color: "white",
      position: "relative",
      transition: "all .2s",
      "&:hover": {
        color: COLORS.blue_1,
        "& svg": {
          color: COLORS.blue_1,
          fill: COLORS.blue_1,
        },
      },
      "&.match": {
        fontWeight: 600,
        color: COLORS.blue_1,
        "& svg": {
          color: COLORS.blue_1,
          fill: COLORS.blue_1,
        },
      },
    },
  };
});

const SidebarItem: React.FunctionComponent<SidebarItemProps> = (props) => {
  const { title, parentURL, Icon, kiddos, allowedUsers } = props;
  const usertypePermission = useUsertypePermission();

  const [open, setOpen] = React.useState<boolean>(false);
  const classes = useStyles();
  const { match, matchKiddo } = useMatchKiddo({ kiddos, parentURL });

  const isExpanded = useAppSelector(
    ({ sidebarStore }) => sidebarStore.sidebarExpand,
  );

  const listedKiddos = React.useMemo(() => {
    if (kiddos && kiddos.length) {
      let allowedKiddos: KiddoElement[] = [];
      kiddos.forEach((kid) => {
        const isKidAllowed = checkIfUsertypeAllowed(
          usertypePermission,
          kid.allowedUsers,
        );
        if (isKidAllowed) {
          allowedKiddos.push(kid);
        }
      });
      return allowedKiddos;
    }
    return undefined;
  }, [kiddos, usertypePermission]);

  const allowed = React.useMemo(() => {
    const isParentAllowed = checkIfUsertypeAllowed(
      usertypePermission,
      allowedUsers,
    );
    if (listedKiddos && listedKiddos.length) {
      return isParentAllowed && listedKiddos.length > 0;
    }
    return isParentAllowed;
  }, [usertypePermission, allowedUsers, listedKiddos]);
  return allowed ? (
    /**
     * @todo buatkan sidebar item ini menjadi sebuah `Link` atau `a`, sehingga menjadi benar secara semantik
     * sehingga ketika item ini diklik kanan, kita bisa buka di new tab.
     */
    <div className={[classes.container].join(" ")}>
      {!kiddos || kiddos.length === 0 ? (
        <Link
          to={{
            pathname: parentURL,
          }}
          className={[
            classes.buttonStyle,
            open ? "open" : undefined,
            match ? "match" : undefined,
            !isExpanded ? "shrink" : undefined,
          ].join(" ")}
        >
          <span
            style={{
              marginRight: isExpanded ? "8.5px" : 0,
              flexBasis: "1rem",
            }}
          >
            {Icon ? <Icon /> : []}
          </span>
          {isExpanded && (
            <span
              className={[classes.title, !isExpanded ? "shrink" : ""].join(" ")}
            >
              <p style={{ margin: 0, width: "fit-content" }}>{title}</p>
            </span>
          )}
        </Link>
      ) : (
        <button
          className={[
            classes.buttonExpandStyle,
            open && "open",
            !isExpanded && "shrink",
          ].join(" ")}
          onClick={(e) => {
            e.stopPropagation();
            setOpen((val) => !val);
          }}
        >
          <span
            style={{
              marginRight: isExpanded ? "8.5px" : 0,
              flexBasis: "1rem",
            }}
          >
            {Icon ? <Icon /> : []}
          </span>
          {isExpanded && (
            <span
              className={[classes.title, !isExpanded ? "shrink" : ""].join(" ")}
            >
              <p style={{ margin: 0, width: "fit-content" }}>{title}</p>
            </span>
          )}
          {listedKiddos && listedKiddos.length ? (
            <span
              style={{
                flexBasis: "1rem",
                display: "flex",
                alignItems: "center",
              }}
            >
              <BsChevronDown
                className={[classes.chevron, open ? "open" : ""].join(" ")}
              />
            </span>
          ) : (
            []
          )}
        </button>
      )}
      {listedKiddos && listedKiddos.length ? (
        <div
          className={[classes.kiddosContainer, open ? "open" : ""].join(" ")}
        >
          {listedKiddos.map(({ title, shrinkTitle, kiddoURL }, i) => {
            return (
              <Link
                to={{
                  pathname: kiddoURL,
                }}
                className={[
                  classes.aKiddo,
                  matchKiddo === kiddoURL ? "match" : "",
                ].join(" ")}
                key={`${i}-${title}`}
              >
                <span
                  className={[classes.title, !isExpanded ? "shrink" : ""].join(
                    " ",
                  )}
                  style={{
                    marginLeft: isExpanded ? "2rem" : 0,
                  }}
                >
                  <p
                    style={{
                      margin: isExpanded ? 0 : "auto",
                      width: "fit-content",
                    }}
                  >
                    {isExpanded ? title : shrinkTitle}
                  </p>
                </span>
              </Link>
            );
          })}
        </div>
      ) : (
        []
      )}
    </div>
  ) : (
    <></>
  );
};

export default SidebarItem;
